import { notifications } from "@mantine/notifications";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  increment,
  Timestamp
} from "firebase/firestore";
import { firestore } from "@/base";
import useUserStore from "@/modules/users/store";
import { getCurrentUser } from "@/services/auth";
import type { TemplateFormData } from "../forms/template-schema";
import type { TemplateListItem } from "../types/template";

export const TEMPLATES_QUERY_KEY = "templates";

/**
 * Converter Timestamp para Date
 * @param timestamp
 */
function timestampToDate(timestamp: Timestamp | undefined): Date {
  if (!timestamp) return new Date();
  return timestamp.toDate();
}

/**
 * Hook para gerenciar templates de relatório
 *
 * Features:
 * - Lista templates ativos (sem deletedAt)
 * - CRUD completo com validação
 * - Optimistic updates
 * - Cache automático via React Query
 */
export function useTemplatesManager() {
  const queryClient = useQueryClient();
  const licenciadaId = useUserStore((state) => state.activeLicenciadaId);
  const currentUser = getCurrentUser();

  // Query: Get all active templates
  /* eslint-disable no-magic-numbers */
  const templatesQuery = useQuery({
    queryKey: [TEMPLATES_QUERY_KEY, licenciadaId],
    queryFn: async (): Promise<TemplateListItem[]> => {
      if (!licenciadaId) return [];

      const templatesReference = collection(firestore, "licenciadas", licenciadaId, "templates_relatorio");
      const q = query(templatesReference, where("deletedAt", "==", null), orderBy("nome", "asc"));

      const snapshot = await getDocs(q);

      return snapshot.docs.map((document_) => {
        const data = document_.data();
        return {
          id: document_.id,
          nome: data.nome,
          descricao: data.descricao || "",
          componentPath: data.componentPath || "DefaultTemplate",
          htmlTemplate: data.htmlTemplate || "",
          imagens: data.imagens || [],
          ativo: data.ativo ?? true,
          acessivelConvidada: data.acessivelConvidada ?? false,
          createdAt: timestampToDate(data.createdAt as Timestamp),
          updatedAt: timestampToDate(data.updatedAt as Timestamp)
        } as TemplateListItem;
      });
    },
    enabled: !!licenciadaId,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });

  // Mutation: Create template
  const createMutation = useMutation({
    mutationFn: async (data: TemplateFormData) => {
      if (!licenciadaId) throw new Error("Licenciada não selecionada");
      if (!currentUser) throw new Error("Usuário não autenticado");

      const templatesReference = collection(firestore, "licenciadas", licenciadaId, "templates_relatorio");

      const templateData = {
        nome: data.nome,
        descricao: data.descricao,
        componentPath: data.componentPath as string,
        htmlTemplate: data.htmlTemplate || "",
        ativo: data.ativo ?? true,
        acessivelConvidada: data.acessivelConvidada ?? false,
        configuracao: data.configuracao || {},
        versao: 1,
        idLicenciada: licenciadaId,
        createdBy: currentUser.uid,
        updatedBy: currentUser.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        deletedAt: null
      };

      const documentReference = await addDoc(templatesReference, templateData);

      return {
        id: documentReference.id,
        ...data,
        createdAt: new Date(),
        updatedAt: new Date()
      } as TemplateListItem;
    },
    onSuccess: (newTemplate) => {
      void queryClient.invalidateQueries({ queryKey: [TEMPLATES_QUERY_KEY, licenciadaId] });
      notifications.show({
        title: "Sucesso",
        message: `Template "${newTemplate.nome}" criado com sucesso`,
        color: "green"
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: "Erro",
        message: error.message || "Erro ao criar template",
        color: "red"
      });
    }
  });

  // Mutation: Update template
  const updateMutation = useMutation({
    mutationFn: async ({
      templateId,
      data,
      silent = false
    }: {
      templateId: string;
      data: TemplateFormData;
      silent?: boolean;
    }) => {
      if (!licenciadaId) throw new Error("Licenciada não selecionada");
      if (!currentUser) throw new Error("Usuário não autenticado");

      const templateReference = doc(firestore, "licenciadas", licenciadaId, "templates_relatorio", templateId);

      // Build update object
      const updateData: Record<string, unknown> = {
        nome: data.nome,
        descricao: data.descricao,
        componentPath: data.componentPath as string,
        ativo: data.ativo,
        acessivelConvidada: data.acessivelConvidada ?? false,
        configuracao: data.configuracao || {},
        updatedBy: currentUser.uid,
        updatedAt: serverTimestamp()
      };

      // Add htmlTemplate if provided
      if (data.htmlTemplate !== undefined) {
        updateData.htmlTemplate = data.htmlTemplate;
        // Increment version when htmlTemplate changes
        updateData.versao = increment(1);
      }

      await updateDoc(templateReference, updateData);

      return { templateId, data, silent };
    },
    onMutate: async ({ templateId, data }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: [TEMPLATES_QUERY_KEY, licenciadaId] });

      // Snapshot previous value
      const previousTemplates = queryClient.getQueryData<TemplateListItem[]>([TEMPLATES_QUERY_KEY, licenciadaId]);

      // Optimistically update cache
      queryClient.setQueryData<TemplateListItem[]>([TEMPLATES_QUERY_KEY, licenciadaId], (old) => {
        if (!old) return old;
        // prettier-ignore
        return old.map((template) =>
          template.id === templateId
            ? {
              ...template,
              ...data,
              updatedAt: new Date()
            }
            : template
        );
      });

      return { previousTemplates };
    },
    onError: (error: Error, _variables, context) => {
      // Rollback on error
      if (context?.previousTemplates) {
        queryClient.setQueryData([TEMPLATES_QUERY_KEY, licenciadaId], context.previousTemplates);
      }
      notifications.show({
        title: "Erro",
        message: error.message || "Erro ao atualizar template",
        color: "red"
      });
    },
    onSuccess: (variables) => {
      // Only show notification if not silent (auto-save)
      if (!variables.silent) {
        notifications.show({
          title: "Sucesso",
          message: `Template "${variables.data.nome}" atualizado com sucesso`,
          color: "green"
        });
      }
    },
    onSettled: (_data, _error, variables) => {
      // Only invalidate queries if not silent (auto-save)
      // Silent updates use optimistic updates only
      if (!variables.silent) {
        void queryClient.invalidateQueries({ queryKey: [TEMPLATES_QUERY_KEY, licenciadaId] });
      }
    }
  });

  // Mutation: Toggle active status
  const toggleActiveMutation = useMutation({
    mutationFn: async ({ templateId, ativo }: { templateId: string; ativo: boolean }) => {
      if (!licenciadaId) throw new Error("Licenciada não selecionada");
      if (!currentUser) throw new Error("Usuário não autenticado");

      const templateReference = doc(firestore, "licenciadas", licenciadaId, "templates_relatorio", templateId);

      await updateDoc(templateReference, {
        ativo,
        updatedBy: currentUser.uid,
        updatedAt: serverTimestamp()
      });

      return { templateId, ativo };
    },
    onMutate: async ({ templateId, ativo }) => {
      await queryClient.cancelQueries({ queryKey: [TEMPLATES_QUERY_KEY, licenciadaId] });

      const previousTemplates = queryClient.getQueryData<TemplateListItem[]>([TEMPLATES_QUERY_KEY, licenciadaId]);

      queryClient.setQueryData<TemplateListItem[]>([TEMPLATES_QUERY_KEY, licenciadaId], (old) => {
        if (!old) return old;
        return old.map((template) =>
          template.id === templateId ? { ...template, ativo, updatedAt: new Date() } : template
        );
      });

      return { previousTemplates };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousTemplates) {
        queryClient.setQueryData([TEMPLATES_QUERY_KEY, licenciadaId], context.previousTemplates);
      }
      notifications.show({
        title: "Erro",
        message: error.message || "Erro ao alterar status do template",
        color: "red"
      });
    },
    onSuccess: ({ ativo }) => {
      notifications.show({
        title: "Sucesso",
        message: `Template ${ativo ? "ativado" : "desativado"} com sucesso`,
        color: "green"
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [TEMPLATES_QUERY_KEY, licenciadaId] });
    }
  });

  // Mutation: Soft delete template
  const deleteMutation = useMutation({
    mutationFn: async (templateId: string) => {
      if (!licenciadaId) throw new Error("Licenciada não selecionada");
      if (!currentUser) throw new Error("Usuário não autenticado");

      const templateReference = doc(firestore, "licenciadas", licenciadaId, "templates_relatorio", templateId);

      await updateDoc(templateReference, {
        deletedAt: serverTimestamp(),
        updatedBy: currentUser.uid,
        updatedAt: serverTimestamp()
      });

      return templateId;
    },
    onMutate: async (templateId) => {
      await queryClient.cancelQueries({ queryKey: [TEMPLATES_QUERY_KEY, licenciadaId] });

      const previousTemplates = queryClient.getQueryData<TemplateListItem[]>([TEMPLATES_QUERY_KEY, licenciadaId]);

      // Optimistically remove from list
      queryClient.setQueryData<TemplateListItem[]>([TEMPLATES_QUERY_KEY, licenciadaId], (old) => {
        if (!old) return old;
        return old.filter((template) => template.id !== templateId);
      });

      return { previousTemplates };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousTemplates) {
        queryClient.setQueryData([TEMPLATES_QUERY_KEY, licenciadaId], context.previousTemplates);
      }
      notifications.show({
        title: "Erro",
        message: error.message || "Erro ao apagar template",
        color: "red"
      });
    },
    onSuccess: () => {
      notifications.show({
        title: "Sucesso",
        message: "Template deletado com sucesso",
        color: "green"
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [TEMPLATES_QUERY_KEY, licenciadaId] });
    }
  });

  return {
    // Query data
    templates: templatesQuery.data ?? [],
    isLoading: templatesQuery.isLoading,
    error: templatesQuery.error,

    // Mutations
    createTemplate: createMutation.mutateAsync,
    updateTemplate: updateMutation.mutateAsync,
    toggleActive: toggleActiveMutation.mutateAsync,
    deleteTemplate: deleteMutation.mutateAsync,

    // Loading states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    // Utilities
    refetch: templatesQuery.refetch
  };
}
